import { useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { AsyncButton, Badge, Card, cx, Empty, ErrorBox, Field, Input, Loading, useToast } from '../../ui/components';

const trend = (value: number | null, goodWhenLower = false): string =>
  value === null || value === 0 ? '' : (value < 0) === goodWhenLower ? 'good' : 'warn';

/** Body composition on the client file: the latest measurement, the change, and the history. */
export function MeasurementsTab({ personId }: { personId: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const data = useApi(() => api<any>(`${T(gym.slug)}/admin/persons/${personId}/measurements`), [personId]);
  const activity = useApi(() => api<any>(`${T(gym.slug)}/admin/persons/${personId}/activity`), [personId]);
  const [adding, setAdding] = useState<Record<string, string>>({});

  if (data.error) return <ErrorBox error={data.error} onRetry={data.reload} />;
  if (!data.data) return <Loading />;
  const { latest, change, measurements } = data.data;

  return (
    <div className="stack">
      <Card title={i18n.t('body.title')} actions={latest ? <span className="small muted">{i18n.date(latest.measuredAt)}</span> : null}>
        {!latest ? <Empty icon="⚖">{i18n.t('body.none')}</Empty> : (
          <>
            <div className="stats">
              <div className="stat"><div className="stat-label">{i18n.t('body.weight')}</div><div className="stat-value">{latest.weightKg ?? '—'} kg</div>
                {change.weightKg !== null && <div className={cx('small', trend(change.weightKg, true))}>{change.weightKg > 0 ? '+' : ''}{change.weightKg} kg</div>}</div>
              <div className="stat"><div className="stat-label">{i18n.t('body.fat')}</div><div className="stat-value">{latest.bodyFatPercent ?? '—'}%</div>
                {change.bodyFatPercent !== null && <div className={cx('small', trend(change.bodyFatPercent, true))}>{change.bodyFatPercent > 0 ? '+' : ''}{change.bodyFatPercent}%</div>}</div>
              <div className="stat"><div className="stat-label">{i18n.t('body.muscle')}</div><div className="stat-value">{latest.skeletalMuscleKg ?? '—'} kg</div>
                {change.skeletalMuscleKg !== null && <div className={cx('small', trend(change.skeletalMuscleKg))}>{change.skeletalMuscleKg > 0 ? '+' : ''}{change.skeletalMuscleKg} kg</div>}</div>
              {latest.inbodyScore !== null && <div className="stat"><div className="stat-label">{i18n.t('body.score')}</div><div className="stat-value">{latest.inbodyScore}</div></div>}
            </div>
            <table className="table">
              <thead><tr>
                <th>{i18n.t('body.date')}</th><th>{i18n.t('body.weight')}</th><th>{i18n.t('body.fat')}</th>
                <th>{i18n.t('body.muscle')}</th><th>{i18n.t('body.visceral')}</th><th>{i18n.t('body.source')}</th><th />
              </tr></thead>
              <tbody>
                {measurements.map((m: any) => (
                  <tr key={m.id}>
                    <td data-label={i18n.t('body.date')}>{i18n.date(m.measuredAt)}</td>
                    <td data-label={i18n.t('body.weight')}>{m.weightKg ?? '—'}</td>
                    <td data-label={i18n.t('body.fat')}>{m.bodyFatPercent ?? '—'}</td>
                    <td data-label={i18n.t('body.muscle')}>{m.skeletalMuscleKg ?? '—'}</td>
                    <td data-label={i18n.t('body.visceral')}>{m.visceralFatLevel ?? '—'}</td>
                    <td data-label={i18n.t('body.source')}><Badge value={m.source} label={i18n.t(`body.source_${m.source}`)} /></td>
                    <td>
                      <AsyncButton size="sm" variant="ghost" confirm={i18n.t('body.delete_confirm')} onClick={async () => {
                        await api(`${T(gym.slug)}/admin/measurements/${m.id}`, { method: 'DELETE' });
                        data.reload();
                      }}>{i18n.t('common.delete')}</AsyncButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      {activity.data && activity.data.connections.length > 0 && (
        <Card title={i18n.t('phone.title')}>
          <div className="stats">
            <div className="stat"><div className="stat-label">{i18n.t('phone.steps')}</div><div className="stat-value">{activity.data.averages.steps ?? '—'}</div></div>
            <div className="stat"><div className="stat-label">{i18n.t('phone.exercise')}</div><div className="stat-value">{activity.data.averages.exerciseMinutes ?? '—'}</div></div>
            <div className="stat"><div className="stat-label">{i18n.t('phone.resting_hr')}</div><div className="stat-value">{activity.data.averages.restingHr ?? '—'}</div></div>
            <div className="stat"><div className="stat-label">{i18n.t('phone.active_days')}</div><div className="stat-value">{activity.data.activeDays}</div></div>
          </div>
          <p className="small muted">
            {activity.data.connections.map((c: any) => `${i18n.t(`phone.source_${c.source}`)}${c.device ? ` (${c.device})` : ''} · ${i18n.t('phone.last_sync', { date: i18n.date(c.lastSyncAt) })}`).join(' · ')}
          </p>
        </Card>
      )}
      <Card title={i18n.t('body.add')}>
        <p className="small muted">{i18n.t('body.add_hint')}</p>
        <div className="grid-3">
          {(['weight', 'pbf', 'smm', 'bmi', 'vfl', 'bmr'] as const).map((k) => (
            <Field key={k} label={i18n.t(`body.field_${k}`)}>
              <Input type="number" step="0.1" value={adding[k] ?? ''} onChange={(e) => setAdding({ ...adding, [k]: e.target.value })} />
            </Field>
          ))}
          <Field label={i18n.t('body.date')}>
            <Input type="datetime-local" value={adding.measuredAt ?? ''} onChange={(e) => setAdding({ ...adding, measuredAt: e.target.value })} />
          </Field>
        </div>
        <AsyncButton disabled={!adding.weight && !adding.pbf} onClick={async () => {
          await api(`${T(gym.slug)}/admin/persons/${personId}/measurements`, {
            body: {
              measuredAt: adding.measuredAt ? new Date(adding.measuredAt).toISOString() : new Date().toISOString(),
              Weight: adding.weight, PBF: adding.pbf, SMM: adding.smm, BMI: adding.bmi, VFL: adding.vfl, BMR: adding.bmr,
            },
          });
          setAdding({});
          toast.ok(i18n.t('body.added'));
          data.reload();
        }}>{i18n.t('body.add')}</AsyncButton>
      </Card>
    </div>
  );
}

/** The gym connects its InBody account, or imports an export file. */
export function InbodySettings() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const cfg = useApi(() => api<any>(`${T(gym.slug)}/admin/inbody`), []);
  const [form, setForm] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any | null>(null);

  if (!cfg.data) return <Loading />;
  const value = (k: string): string => form[k] ?? String(cfg.data[k] ?? '');

  return (
    <div className="stack">
      <Card title={i18n.t('body.inbody')} actions={<Badge value={cfg.data.connected ? 'good' : 'neutral'} label={i18n.t(cfg.data.connected ? 'body.connected' : 'body.not_connected')} />}>
        <p className="small muted">{i18n.t('body.inbody_hint')}</p>
        <div className="grid-2">
          <Field label={i18n.t('body.base_url')} hint={i18n.t('body.base_url_hint')}>
            <Input value={value('baseUrl')} placeholder="https://api.lookinbody.com" onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} />
          </Field>
          <Field label={i18n.t('body.account')}><Input value={value('accountName')} onChange={(e) => setForm({ ...form, accountName: e.target.value })} /></Field>
          <Field label={i18n.t('body.api_key')} hint={cfg.data.apiKeySet ? i18n.t('body.key_set') : undefined}>
            <Input type="password" value={form.apiKey ?? ''} placeholder="••••" onChange={(e) => setForm({ ...form, apiKey: e.target.value })} />
          </Field>
          <Field label={i18n.t('body.results_path')} hint={i18n.t('body.results_path_hint')}>
            <Input value={value('resultsPath')} placeholder="/Datas" onChange={(e) => setForm({ ...form, resultsPath: e.target.value })} />
          </Field>
        </div>
        <div className="row gap-sm wrap">
          <AsyncButton onClick={async () => { await api(`${T(gym.slug)}/admin/inbody`, { method: 'PUT', body: form }); setForm({}); cfg.reload(); toast.ok(i18n.t('common.saved')); }}>{i18n.t('common.save')}</AsyncButton>
          <AsyncButton variant="secondary" disabled={!cfg.data.connected} onClick={async () => {
            const r = await api(`${T(gym.slug)}/admin/inbody/sync`, { body: {} });
            toast.ok(i18n.t('body.synced', { imported: r.imported, found: r.found }));
          }}>{i18n.t('body.sync_now')}</AsyncButton>
        </div>
        {cfg.data.lastSyncAt && <p className="small muted">{i18n.t('body.last_sync', { date: i18n.dateTime(cfg.data.lastSyncAt) })}</p>}
      </Card>

      <Card title={i18n.t('body.push')}>
        <p className="small muted">{i18n.t('body.push_hint')}</p>
        <Input readOnly value={cfg.data.webhookUrl} aria-label={i18n.t('body.push')} />
        <AsyncButton size="sm" variant="secondary" onClick={async () => {
          await api(`${T(gym.slug)}/admin/inbody`, { method: 'PUT', body: { rotateWebhookKey: true } });
          cfg.reload();
          toast.ok(i18n.t('body.key_rotated'));
        }}>{i18n.t(cfg.data.webhookKeySet ? 'body.new_key' : 'body.make_key')}</AsyncButton>
      </Card>

      <Card title={i18n.t('body.import')}>
        <p className="small muted">{i18n.t('body.import_hint')}</p>
        <input type="file" accept=".csv,text/csv" aria-label={i18n.t('body.import')} onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          setResult(await api(`${T(gym.slug)}/admin/inbody/import`, { body: { csv: await f.text(), dryRun: true } }).catch((err) => { toast.error(err); return null; }));
          (window as any).__csv = await f.text();
        }} />
        {result && (
          <div className="stack-sm">
            <p>{i18n.t('body.import_preview', { imported: result.imported, unmatched: result.unmatched.length })}</p>
            {result.unmatched.length > 0 && <ul className="sets">{result.unmatched.slice(0, 5).map((u: any, i: number) => <li key={i}>{String(u.row)} · {i18n.date(u.measuredAt)}</li>)}</ul>}
            <AsyncButton onClick={async () => {
              const r = await api(`${T(gym.slug)}/admin/inbody/import`, { body: { csv: (window as any).__csv } });
              toast.ok(i18n.t('body.imported', { imported: r.imported, duplicates: r.duplicates }));
              setResult(null);
            }}>{i18n.t('body.import_now')}</AsyncButton>
          </div>
        )}
      </Card>
    </div>
  );
}
